import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  Download,
  Pencil,
  Plus,
  ScanBarcode,
  StopCircle,
  Trash2,
  Loader2,
  Search,
  X,
  Save,
} from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { FaqSection } from "@/components/seo/FaqSection";
import { toast } from "sonner";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript } from "@/lib/seo";
import {
  startCameraScan,
  formatLabel,
  type CameraController,
  type DecodeResult,
} from "@/lib/zxing";

const PATH = "/inventory-scanner";
const TITLE = "Inventory Scanner — Free Browser Barcode Inventory";
const DESC =
  "Free inventory scanner that turns your phone or webcam into a barcode inventory system. Scan, edit, track quantity and location, export CSV — all local.";

const STORAGE_KEY = "readbarcode.inventory.v1";

const FAQ = [
  {
    q: "Is this inventory scanner really free, with no login?",
    a: "Yes. There's no account, no trial, and no upload. The inventory scanner runs entirely in your browser. Items live in your browser's localStorage on the device you used to add them.",
  },
  {
    q: "Where Is My Inventory Data Stored?",
    a: "Locally, in this browser's localStorage on this device. We never send your items, quantities, or notes to a server. Clearing your browser data or switching devices will remove the list — export to CSV first if you need a backup.",
  },
  {
    q: "Which barcode types can I scan into the inventory?",
    a: "Any 1D or 2D format that this site's underlying decoder supports — EAN-8/13, UPC-A/E, Code 128, Code 39, ITF, Codabar, QR, Data Matrix, Aztec, PDF417. You can also paste or type any barcode value manually, so unsupported formats still work.",
  },
  {
    q: "Can I Use This as a Warehouse Barcode Scanner on My Phone?",
    a: "Yes — it's designed mobile-first. Open the page on your phone, tap Start camera, and scan items down an aisle. Each scan auto-fills the barcode field so you only need to add the product name, quantity, and location.",
  },
  {
    q: "How Do I Export My Inventory List?",
    a: "Click Export CSV. You'll get a standard comma-separated file with columns for barcode, product name, quantity, location, notes, and the time the item was last updated. Open it in Excel, Google Sheets, or import into any inventory or ERP tool.",
  },
  {
    q: "Can Two People Share the Same Inventory List?",
    a: "Not yet — this MVP is a single-device tool with no login. Two people can each maintain their own list and merge the CSV exports. A multi-user, cloud-synced version is on the roadmap.",
  },
  {
    q: "How Does a Barcode Inventory System Work?",
    a: "Each product carries a barcode whose value is the key to one row in a list or database. Scanning the code finds that row, and you add or subtract the quantity you counted, received or sold. On this page the key is the exact barcode text, so scanning a code that is already listed highlights its row instead of adding a new one.",
  },
  {
    q: "Is There a Free Barcode Scanner for Inventory?",
    a: "Yes. This inventory scanner is free, needs no account and works in a phone or desktop browser with the camera or a USB wedge scanner. The trade-off is that the list stays on one device, so export the CSV regularly and import it elsewhere when you need shared or permanent records.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "GS1 & Inventory", path: "/gs1-inventory" },
  { name: "Inventory Scanner", path: PATH },
];

export const Route = createFileRoute("/inventory-scanner")({
  component: InventoryScannerPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Inventory Scanner",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

type InventoryItem = {
  id: string;
  barcode: string;
  name: string;
  quantity: number;
  location: string;
  notes: string;
  updatedAt: number;
};

function InventoryScannerPage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Inventory Scanner"
      description="Turn any phone or laptop camera into a free barcode inventory system. Scan or type a barcode, track product name, quantity, location and notes, and export to CSV — all kept locally on your device. Scanning a barcode that is already on the list highlights its row, and adding it again raises the quantity instead of creating a duplicate."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About this inventory scanner"
            about={
              <>
                <p>
                  This inventory scanner is a free, browser-based barcode inventory system. It pairs a
                  one-tap camera scanner with a simple editable table so you can build a stock list
                  without signing up, installing software, or paying a subscription. Everything stays in
                  your browser — useful for small shops, makers, classrooms, home cataloguing, and field
                  inventory checks.
                </p>
                <p>
                  The same decoder behind our <Link to="/barcode-reader">online barcode reader</Link> powers the camera here, so it handles standard retail and logistics symbologies. For
                  more detailed validation, open the <Link to="/gtin-validator">GTIN validator</Link> alongside this page.
                </p>
              </>
            }
            whenToUse={[
              "Doing a quick stock-take in a small shop without a full POS system.",
              "Cataloguing books, tools, parts, or hobby supplies at home.",
              "Building a warehouse barcode scanner workflow on a spare phone before committing to enterprise software.",
              "Scanning incoming deliveries into a CSV that you can hand to accounting.",
              "Teaching students how a barcode inventory system works, end to end.",
            ]}
            howItWorks={[
              {
                title: "Start the camera",
                desc: "Grant camera access once and point at a barcode. The scanner decodes it and auto-fills the barcode field.",
              },
              {
                title: "Or type manually",
                desc: "No camera? Paste or type the barcode directly into the form — handy for keyboards and wedge scanners.",
              },
              {
                title: "Add product details",
                desc: "Give the item a name, quantity, location, and optional notes. Defaults to quantity 1.",
              },
              {
                title: "Edit or delete",
                desc: "Each row has edit and delete actions. Quantities can be bumped in place as stock moves.",
              },
              {
                title: "Export to CSV",
                desc: "Download the full list as a CSV any spreadsheet, accounting or ERP tool can import.",
              },
            ]}
            whatToAvoid={[
              "Treating this as your only copy. Export to CSV regularly — clearing browser data wipes the list.",
              "Assuming the same list appears on another device. Without a login, each browser has its own inventory.",
              "Scanning barcodes through thick plastic wrap — glare often beats the decoder; tilt or remove the wrap.",
              "Using emoji or commas inside the notes field if you plan to re-import the CSV into a strict parser.",
              "Relying on the scanner alone to validate GTINs. For check-digit confirmation use the GTIN validator.",
            ]}
            tips={[
              "Bookmark this page on your phone's home screen — it behaves like a lightweight inventory app.",
              'Use the Location column consistently (e.g. "A1-03") so you can sort by it in a spreadsheet later.',
              "If you scan the same barcode twice, the existing row is highlighted so you can increment quantity instead of duplicating.",
              "Export CSV after each major session — treat the local list as working memory, not the source of truth.",
              "On laptops, a USB wedge scanner types straight into the barcode field. The camera tab isn't required.",
            ]}
          />
          <h2>How to Track Inventory With a Barcode Scanner</h2>
          <p>To track inventory with a barcode scanner, give every item a barcode, scan each one into a list with its quantity and location, and export the list to compare counts over time.</p>
          <p>Retail goods usually arrive with a UPC or EAN printed on them. Loose parts and homemade stock need a label, and an internal Code 128 label from the <Link to="/barcode-generator">barcode generator</Link> scans here like any other code.</p>
          <p>Give each product variant its own barcode. Rows are matched on the exact barcode value, so two sizes that share one code end up merged into a single row.</p>
          <h3>Count Inventory With Your Phone</h3>
          <p>Open this page on your phone, tap Start camera and allow camera access; the scanner asks for the rear camera first. It keeps reading frames, so each barcode it decodes drops straight into the Barcode field.</p>
          <ol>
            <li>Point the camera at the first barcode on the shelf.</li>
            <li>Type the quantity you count, plus the shelf or bin code in Location, such as A1-03.</li>
            <li>Tap Add to inventory. The form clears for the next item, so enter the location again each time.</li>
            <li>If the barcode is already listed, its row is highlighted and adding it again raises that row&apos;s quantity by the number you entered.</li>
            <li>Use the search box to find a row by barcode, name, location or notes when a count needs correcting.</li>
          </ol>
          <p>For a full barcode inventory count, work one aisle at a time and export after each aisle. The list is saved in this browser after every change, but a fresh CSV protects you if the browser data is cleared.</p>
          <p>The same routine works as a home inventory barcode scanner for a pantry, a tool wall or a book collection. The Notes field can hold a purchase date, a condition or a warranty reminder.</p>
          <h3>Scan Barcode Inventory Into Excel</h3>
          <p>Excel opens the CSV export directly, so you can scan barcode inventory here and finish the work in a spreadsheet. The file has six columns: barcode, name, quantity, location, notes and updated_at, with the time written in ISO 8601 format in UTC.</p>
          <ol>
            <li>Tap Export CSV; the file downloads as inventory- followed by the date.</li>
            <li>In Excel, open a blank workbook and choose Data, then From Text/CSV.</li>
            <li>Select the file, choose Transform Data and set the barcode column type to Text.</li>
            <li>Load the data, then total quantities by barcode with a PivotTable or SUMIF.</li>
          </ol>
          <p>Setting the barcode column to Text keeps leading zeros. A UPC-A such as 012345678905 loses its first digit when Excel reads it as a number, and the shortened value no longer matches the product.</p>
          <h2>A Simple Barcode Inventory System for Small Business</h2>
          <p>A simple barcode inventory system for a small business needs barcodes on the stock, a scanner to read them and a list that holds quantity and location for each code. This page supplies the scanner and the list as a free inventory barcode scanning system for shops that count stock now and then.</p>
          <p>A free barcode scanner for inventory has limits that matter more as you grow. The list lives in one browser on one device, there is no login or change history, and it does not connect to a till or online store, so a sale does not lower the stock level.</p>
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th scope="col">Need</th>
                  <th scope="col">How this inventory scanner handles it</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Scanning</td>
                  <td>Phone or laptop camera, or a USB wedge scanner typing into the Barcode field</td>
                </tr>
                <tr>
                  <td>Quantity and location</td>
                  <td>One row per barcode with quantity, location and notes</td>
                </tr>
                <tr>
                  <td>Backup</td>
                  <td>CSV export; the working list stays in one browser</td>
                </tr>
                <tr>
                  <td>Several users</td>
                  <td>Not supported; each person keeps a list and you merge the CSV files</td>
                </tr>
                <tr>
                  <td>Sales and reorder alerts</td>
                  <td>Not supported; import the CSV into your POS or ERP</td>
                </tr>
              </tbody>
            </table>
          </TableScroll>
          <p>For products you make and sell, the barcodes on the packaging should be GTINs licensed from GS1, since many retailers and marketplaces require them. Check each code with the GTIN validator before printing, and run a long supplier list through the <Link to="/batch-barcode-scanner">batch barcode scanner</Link> to catch bad check digits and duplicates in one pass.</p>
          <p>To use this page like an inventory barcode scanner app, add it to your home screen. The <Link to="/read-barcode-app">read barcode app</Link> page lists the install steps for iPhone, Android and desktop.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <InventoryApp />
    </ToolLayout>
  );
}

/* -------------------- App -------------------- */

function newId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function loadItems(): InventoryItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((it) => it && typeof it.id === "string" && typeof it.barcode === "string");
  } catch {
    return [];
  }
}

function saveItems(items: InventoryItem[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* quota — ignore */
  }
}

function InventoryApp() {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    barcode: string;
    name: string;
    quantity: string;
    location: string;
    notes: string;
  }>({ barcode: "", name: "", quantity: "1", location: "", notes: "" });
  const barcodeInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setItems(loadItems());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (hydrated) saveItems(items);
  }, [items, hydrated]);

  const upsertScanned = useCallback(
    (barcode: string) => {
      const trimmed = barcode.trim();
      if (!trimmed) return;
      const existing = items.find((it) => it.barcode === trimmed);
      if (existing) {
        setHighlightId(existing.id);
        setDraft((d) => ({ ...d, barcode: trimmed }));
        toast.info(`"${trimmed}" is already in the list`, {
          description: "Increment its quantity or edit it directly.",
        });
      } else {
        setDraft((d) => ({ ...d, barcode: trimmed }));
        toast.success(`Scanned ${trimmed}`);
      }
      barcodeInputRef.current?.focus();
    },
    [items],
  );

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const barcode = draft.barcode.trim();
    if (!barcode) {
      toast.error("Add a barcode value first.");
      return;
    }
    const qty = Math.max(0, Math.floor(Number(draft.quantity)) || 0);
    const existing = items.find((it) => it.barcode === barcode);
    if (existing) {
      setItems((prev) =>
        prev.map((it) =>
          it.id === existing.id
            ? {
                ...it,
                quantity: it.quantity + (qty || 1),
                name: draft.name.trim() || it.name,
                location: draft.location.trim() || it.location,
                notes: draft.notes.trim() || it.notes,
                updatedAt: Date.now(),
              }
            : it,
        ),
      );
      setHighlightId(existing.id);
      toast.success(`Updated "${barcode}"`);
    } else {
      const item: InventoryItem = {
        id: newId(),
        barcode,
        name: draft.name.trim(),
        quantity: qty || 1,
        location: draft.location.trim(),
        notes: draft.notes.trim(),
        updatedAt: Date.now(),
      };
      setItems((prev) => [item, ...prev]);
      setHighlightId(item.id);
      toast.success(`Added "${barcode}"`);
    }
    setDraft({ barcode: "", name: "", quantity: "1", location: "", notes: "" });
    barcodeInputRef.current?.focus();
  };

  const onDelete = (id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const onClearAll = () => {
    if (items.length === 0) return;
    if (
      !window.confirm(
        `Delete all ${items.length} inventory item${items.length === 1 ? "" : "s"}? This can't be undone.`,
      )
    )
      return;
    setItems([]);
    toast.success("Inventory cleared");
  };

  const onExport = () => {
    if (items.length === 0) {
      toast.error("Nothing to export yet.");
      return;
    }
    const csv = toCsv(items);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      [it.barcode, it.name, it.location, it.notes].some((v) => v.toLowerCase().includes(q)),
    );
  }, [items, query]);

  const totalUnits = useMemo(() => items.reduce((sum, it) => sum + (it.quantity || 0), 0), [items]);

  return (
    <div className="grid gap-6">
      <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
        <CameraCard onScanned={upsertScanned} />
        <Card>
          <CardContent className="p-5 md:p-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Add item
            </p>
            <form className="mt-4 grid gap-3" onSubmit={onAdd}>
              <div className="grid gap-1.5">
                <label htmlFor="inv-barcode" className="text-sm font-medium">
                  Barcode
                </label>
                <Input
                  id="inv-barcode"
                  ref={barcodeInputRef}
                  value={draft.barcode}
                  onChange={(e) => setDraft((d) => ({ ...d, barcode: e.target.value }))}
                  placeholder="Scan or type barcode value"
                  autoComplete="off"
                />
              </div>
              <div className="grid gap-1.5">
                <label htmlFor="inv-name" className="text-sm font-medium">
                  Product name
                </label>
                <Input
                  id="inv-name"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <label htmlFor="inv-qty" className="text-sm font-medium">
                    Quantity
                  </label>
                  <Input
                    id="inv-qty"
                    type="number"
                    min={0}
                    step={1}
                    value={draft.quantity}
                    onChange={(e) => setDraft((d) => ({ ...d, quantity: e.target.value }))}
                  />
                </div>
                <div className="grid gap-1.5">
                  <label htmlFor="inv-loc" className="text-sm font-medium">
                    Location
                  </label>
                  <Input
                    id="inv-loc"
                    value={draft.location}
                    onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))}
                    placeholder="e.g. A1-03"
                  />
                </div>
              </div>
              <div className="grid gap-1.5">
                <label htmlFor="inv-notes" className="text-sm font-medium">
                  Notes
                </label>
                <Textarea
                  id="inv-notes"
                  value={draft.notes}
                  onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                  placeholder="Optional"
                  rows={2}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="submit">
                  <Plus className="mr-2 h-4 w-4" /> Add to inventory
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    setDraft({
                      barcode: "",
                      name: "",
                      quantity: "1",
                      location: "",
                      notes: "",
                    })
                  }
                >
                  Clear form
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-5 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-base font-semibold md:text-lg">
                Inventory{" "}
                <Badge variant="secondary" className="ml-1 align-middle">
                  {items.length} {items.length === 1 ? "item" : "items"}
                </Badge>{" "}
                <Badge variant="outline" className="ml-1 align-middle">
                  {totalUnits} {totalUnits === 1 ? "unit" : "units"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Saved locally in this browser. Export to CSV for a backup.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search barcode, name, location…"
                  className="w-56 pl-8"
                />
              </div>
              <Button variant="outline" onClick={onExport}>
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </Button>
              <Button variant="ghost" onClick={onClearAll} disabled={items.length === 0}>
                <Trash2 className="mr-2 h-4 w-4" /> Clear all
              </Button>
            </div>
          </div>

          <div className="mt-5">
            {items.length === 0 ? (
              <EmptyTable />
            ) : (
              <InventoryTable
                items={filtered}
                highlightId={highlightId}
                onEdit={(it) => setEditing(it)}
                onDelete={onDelete}
              />
            )}
            {items.length > 0 && filtered.length === 0 && (
              <div className="mt-4 rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
                No items match "{query}".
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <EditDialog
        item={editing}
        onClose={() => setEditing(null)}
        onSave={(updated) => {
          setItems((prev) =>
            prev.map((it) => (it.id === updated.id ? { ...updated, updatedAt: Date.now() } : it)),
          );
          setEditing(null);
          setHighlightId(updated.id);
          toast.success("Item updated");
        }}
      />
    </div>
  );
}

function EmptyTable() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-10 text-center">
      <ScanBarcode className="h-7 w-7 text-muted-foreground" aria-hidden />
      <div className="text-sm font-medium">No inventory yet</div>
      <p className="max-w-md text-sm text-muted-foreground">
        Scan a barcode with the camera, or type one into the form above to add your first item.
      </p>
    </div>
  );
}

function InventoryTable({
  items,
  highlightId,
  onEdit,
  onDelete,
}: {
  items: InventoryItem[];
  highlightId: string | null;
  onEdit: (it: InventoryItem) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[640px] text-left text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Barcode</th>
            <th className="px-3 py-2 font-medium">Product</th>
            <th className="px-3 py-2 font-medium text-right">Qty</th>
            <th className="px-3 py-2 font-medium">Location</th>
            <th className="px-3 py-2 font-medium">Notes</th>
            <th className="px-3 py-2 font-medium" aria-label="actions" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {items.map((it) => (
            <tr
              key={it.id}
              className={it.id === highlightId ? "bg-primary/5" : "bg-background hover:bg-muted/30"}
            >
              <td className="px-3 py-2 font-mono text-xs">{it.barcode}</td>
              <td className="px-3 py-2">
                {it.name || <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-3 py-2 text-right font-medium">{it.quantity}</td>
              <td className="px-3 py-2">
                {it.location || <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                <span className="line-clamp-1">{it.notes || "—"}</span>
              </td>
              <td className="px-3 py-2">
                <div className="flex justify-end gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => onEdit(it)}
                    aria-label="Edit item"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => onDelete(it.id)}
                    aria-label="Delete item"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EditDialog({
  item,
  onClose,
  onSave,
}: {
  item: InventoryItem | null;
  onClose: () => void;
  onSave: (updated: InventoryItem) => void;
}) {
  const [form, setForm] = useState<InventoryItem | null>(item);
  useEffect(() => setForm(item), [item]);
  if (!form) return null;
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Inventory Item</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <label className="text-sm font-medium" htmlFor="edit-barcode">
              Barcode
            </label>
            <Input
              id="edit-barcode"
              value={form.barcode}
              onChange={(e) => setForm((f) => (f ? { ...f, barcode: e.target.value } : f))}
            />
          </div>
          <div className="grid gap-1.5">
            <label className="text-sm font-medium" htmlFor="edit-name">
              Product name
            </label>
            <Input
              id="edit-name"
              value={form.name}
              onChange={(e) => setForm((f) => (f ? { ...f, name: e.target.value } : f))}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <label className="text-sm font-medium" htmlFor="edit-qty">
                Quantity
              </label>
              <Input
                id="edit-qty"
                type="number"
                min={0}
                step={1}
                value={form.quantity}
                onChange={(e) =>
                  setForm((f) =>
                    f
                      ? {
                          ...f,
                          quantity: Math.max(0, Math.floor(Number(e.target.value)) || 0),
                        }
                      : f,
                  )
                }
              />
            </div>
            <div className="grid gap-1.5">
              <label className="text-sm font-medium" htmlFor="edit-loc">
                Location
              </label>
              <Input
                id="edit-loc"
                value={form.location}
                onChange={(e) => setForm((f) => (f ? { ...f, location: e.target.value } : f))}
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <label className="text-sm font-medium" htmlFor="edit-notes">
              Notes
            </label>
            <Textarea
              id="edit-notes"
              value={form.notes}
              rows={3}
              onChange={(e) => setForm((f) => (f ? { ...f, notes: e.target.value } : f))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            <X className="mr-2 h-4 w-4" /> Cancel
          </Button>
          <Button
            onClick={() => {
              if (!form.barcode.trim()) {
                toast.error("Barcode can't be empty");
                return;
              }
              onSave(form);
            }}
          >
            <Save className="mr-2 h-4 w-4" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Camera card -------------------- */

function CameraCard({ onScanned }: { onScanned: (barcode: string) => void }) {
  const [tab, setTab] = useState<"camera" | "manual">("camera");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controllerRef = useRef<CameraController | null>(null);
  const [status, setStatus] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "scanning" }
    | { kind: "error"; message: string }
    | { kind: "last"; result: DecodeResult }
  >({ kind: "idle" });

  const stop = useCallback(() => {
    controllerRef.current?.stop();
    controllerRef.current = null;
  }, []);

  useEffect(() => {
    if (tab !== "camera") stop();
    return () => stop();
  }, [tab, stop]);

  const start = async () => {
    if (!videoRef.current) return;
    setStatus({ kind: "loading" });
    try {
      const ctrl = await startCameraScan(
        videoRef.current,
        (r) => {
          onScanned(r.text);
          setStatus({ kind: "last", result: r });
        },
        (err) => {
          stop();
          setStatus({ kind: "error", message: err.message });
        },
      );
      controllerRef.current = ctrl;
      setStatus({ kind: "scanning" });
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.name === "NotAllowedError"
            ? "Camera permission denied. Allow camera access and try again."
            : err.message
          : "Could not start the camera.";
      setStatus({ kind: "error", message: msg });
    }
  };

  return (
    <Card>
      <CardContent className="p-5 md:p-6">
        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="camera">
              <Camera className="mr-2 h-4 w-4" /> Camera
            </TabsTrigger>
            <TabsTrigger value="manual">
              <ScanBarcode className="mr-2 h-4 w-4" /> Manual / wedge
            </TabsTrigger>
          </TabsList>
          <TabsContent value="camera" className="mt-5">
            <div className="relative overflow-hidden rounded-lg border border-border bg-muted">
              <video
                ref={videoRef}
                className="aspect-video w-full bg-black object-cover"
                playsInline
                muted
              />
              {status.kind !== "scanning" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/80 p-6 text-center">
                  <Camera className="h-8 w-8 text-muted-foreground" />
                  <div className="text-sm text-muted-foreground">
                    Continuous scan — each decoded value is added to the form automatically.
                  </div>
                </div>
              )}
              {status.kind === "scanning" && (
                <div className="pointer-events-none absolute inset-x-6 top-1/2 h-0.5 -translate-y-1/2 bg-primary/80 shadow-[0_0_12px_var(--color-primary)]" />
              )}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {status.kind !== "scanning" ? (
                <Button onClick={start} disabled={status.kind === "loading"}>
                  {status.kind === "loading" ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Camera className="mr-2 h-4 w-4" />
                  )}
                  Start camera
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    stop();
                    setStatus({ kind: "idle" });
                  }}
                  variant="outline"
                >
                  <StopCircle className="mr-2 h-4 w-4" /> Stop camera
                </Button>
              )}
              {status.kind === "last" && (
                <span className="text-xs text-muted-foreground">
                  Last:{" "}
                  <Badge variant="secondary" className="font-mono">
                    {status.result.text}
                  </Badge>{" "}
                  ({formatLabel(status.result.format)})
                </span>
              )}
              {status.kind === "error" && (
                <span className="text-xs text-destructive">{status.message}</span>
              )}
            </div>
          </TabsContent>
          <TabsContent value="manual" className="mt-5">
            <div className="rounded-lg border border-dashed border-border bg-muted/30 p-5 text-sm text-muted-foreground">
              <p>
                Type or paste a barcode into the <strong>Barcode</strong> field on the right, then
                press{" "}
                <kbd className="rounded border border-border bg-background px-1 text-xs">Enter</kbd>{" "}
                or click <strong>Add to inventory</strong>. USB wedge scanners type straight in — no
                camera permission needed.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

/* -------------------- CSV -------------------- */

function csvEscape(value: string | number): string {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function toCsv(items: InventoryItem[]): string {
  const header = ["barcode", "name", "quantity", "location", "notes", "updated_at"];
  const rows = items.map((it) =>
    [it.barcode, it.name, it.quantity, it.location, it.notes, new Date(it.updatedAt).toISOString()]
      .map(csvEscape)
      .join(","),
  );
  return [header.join(","), ...rows].join("\n") + "\n";
}

/* -------------------- Related -------------------- */

function RelatedTools() {
  const tools: { to: string; label: string; desc: string }[] = [
    {
      to: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Decode a single barcode from camera, image, or URL.",
    },
    {
      to: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Check that GTIN-8/12/13/14 barcodes have the right check digit before they enter your inventory.",
    },
    {
      to: "/upc-lookup",
      label: "UPC Lookup",
      desc: "Validate UPC-A codes scanned from US retail products.",
    },
    {
      to: "/ean-lookup",
      label: "EAN Lookup",
      desc: "Validate EAN-8 and EAN-13 codes from international retail products.",
    },
    {
      to: "/batch-barcode-scanner",
      label: "Batch Barcode Scanner",
      desc: "Clean, deduplicate and check a pasted list of barcodes, then export it to CSV.",
    },
    {
      to: "/read-barcode-app",
      label: "Read Barcode App",
      desc: "Install ReadBarcode on your phone or desktop so the scanners open from the home screen.",
    },
    {
      to: "/barcode-guides/sunrise-2027-explained",
      label: "Sunrise 2027 Guide",
      desc: "Plan ahead for 2D barcode rollout in retail and warehousing.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-xl font-semibold tracking-tight md:text-2xl">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {tools.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            className="group rounded-lg border border-border bg-background p-4 transition hover:border-primary/50 hover:bg-muted/40"
          >
            <div className="text-sm font-semibold">{t.label}</div>
            <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}
